import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { wc26Service } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { RefreshCw, Trophy, Clock, Tv } from 'lucide-react';

/**
 * Page GROUPES — World Cup 2026
 * Affiche les 12 groupes avec classements + matchs + scores LIVE
 */
export const GroupsView = () => {
  const [standings, setStandings] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadAll = useCallback(async (force = false) => {
    try {
      const [stRes, fxRes, lvRes] = await Promise.all([
        wc26Service.getStandings(force),
        wc26Service.getFixtures(force),
        wc26Service.getLive(),
      ]);
      setStandings(stRes.standings || []);
      setFixtures(fxRes.fixtures || []);
      setLive(lvRes.live || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('[wc26] loadAll failed', err);
      toast.error('Impossible de charger les groupes');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadAll(false);
  }, [loadAll]);

  // Auto-refresh LIVE toutes les 30s (uniquement si match en cours)
  // 🆕 Détecte aussi les transitions live→finished pour force-refresh standings/fixtures
  const prevLiveStatusesRef = React.useRef([]);
  useEffect(() => {
    if (live.length === 0) {
      // Si on avait des live et qu'il n'y en a plus → match(s) vient de finir → force refresh standings
      if (prevLiveStatusesRef.current.length > 0) {
        prevLiveStatusesRef.current = [];
        (async () => {
          try {
            await wc26Service.refresh(); // force backend refresh (vide les caches)
            await loadAll(true);
            toast.success('🏁 Match terminé — classements mis à jour');
          } catch { /* ignore */ }
        })();
      }
      return;
    }
    // Sauvegarde l'état actuel des matchs live
    prevLiveStatusesRef.current = live.map(m => ({ id: m.fixture_id, status: m.status_short }));

    const interval = setInterval(async () => {
      try {
        // Refresh live
        const r = await wc26Service.getLive();
        const newLive = r.live || [];
        // Détecte si un match qui était live est maintenant FT/AET/PEN (terminé)
        const prevIds = new Set(prevLiveStatusesRef.current.map(p => p.id));
        const newIds = new Set(newLive.map(m => m.fixture_id));
        const justFinished = [...prevIds].some(id => !newIds.has(id));
        setLive(newLive);
        prevLiveStatusesRef.current = newLive.map(m => ({ id: m.fixture_id, status: m.status_short }));

        if (justFinished) {
          // Un match vient de finir → force refresh standings + fixtures
          await wc26Service.refresh();
          await loadAll(true);
          toast.success('🏁 Match terminé — classements & matchs mis à jour');
        }
      } catch {/* ignore */}
    }, 30000);
    return () => clearInterval(interval);
  }, [live.length, loadAll]);

  // Auto-refresh standings + fixtures toutes les 2 minutes quand il y a du live (en plus du polling 30s)
  useEffect(() => {
    if (live.length === 0) return;
    const interval = setInterval(() => { loadAll(false); }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [live.length, loadAll]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await wc26Service.refresh();
      toast.success(`✅ Sync OK — ${r.standings_count} groupes / ${r.fixtures_count} matchs / ${r.live_count} live`);
      await loadAll(false);
    } catch (err) {
      toast.error('Sync échouée');
    } finally {
      setRefreshing(false);
    }
  };

  // Index fixtures par groupe (parsing du round "Group Stage - N")
  const fixturesByGroup = useMemo(() => {
    const byTeam = {};
    fixtures.forEach(fx => {
      if (!fx.round?.startsWith('Group Stage')) return;
      [fx.home_team, fx.away_team].forEach(t => {
        if (!t) return;
        if (!byTeam[t]) byTeam[t] = [];
        byTeam[t].push(fx);
      });
    });
    // Pour chaque groupe, on récupère les matchs de toutes les équipes du groupe
    const byGroup = {};
    standings.forEach(grp => {
      const teamNames = grp.teams.map(t => t.team);
      const matches = new Map(); // dedupe par fixture_id
      teamNames.forEach(tn => {
        (byTeam[tn] || []).forEach(fx => matches.set(fx.fixture_id, fx));
      });
      byGroup[grp.group] = Array.from(matches.values()).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    });
    return byGroup;
  }, [fixtures, standings]);

  const liveFixtureIds = useMemo(() => new Set(live.map(l => l.fixture_id)), [live]);

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-48 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2" data-testid="groups-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Trophy className="w-7 h-7 text-yellow-500" />
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">World Cup 2026 — Groupes</h2>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {standings.length} groupes · {fixtures.length} matchs · {live.length > 0 ? (
                <span className="text-red-500 font-semibold">🔴 {live.length} LIVE</span>
              ) : 'aucun match en cours'}
              {lastUpdate && <span className="ml-2">· dernière sync : {lastUpdate.toLocaleTimeString('fr-FR')}</span>}
            </p>
          </div>
        </div>
        <Button onClick={handleManualRefresh} disabled={refreshing} variant="outline" size="sm" data-testid="wc26-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Synchroniser
        </Button>
      </div>

      {/* Banner LIVE en grand si match en cours */}
      {live.length > 0 && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4" data-testid="wc26-live-banner">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            <span className="font-bold text-red-700 dark:text-red-300">EN DIRECT</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {live.map(l => (
              <div key={l.fixture_id} className="bg-white dark:bg-zinc-900 rounded-md p-3 shadow-sm" data-testid={`wc26-live-${l.fixture_id}`}>
                <div className="text-xs text-zinc-500 mb-1">{l.round}</div>
                <div className="flex items-center justify-between font-semibold">
                  <span>{l.home_team}</span>
                  <span className="text-xl font-bold text-red-600 dark:text-red-400">{l.home_goals ?? 0} - {l.away_goals ?? 0}</span>
                  <span>{l.away_team}</span>
                </div>
                <div className="text-xs text-center mt-1 text-zinc-500">
                  {l.elapsed ? `${l.elapsed}'` : l.status_long}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grille des groupes (12 groupes A à L uniquement, exclut "Group Stage" qui est le classement des 3èmes) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {standings.filter(g => g.group && /^Group [A-L]$/.test(g.group)).map(grp => {
          const groupMatches = fixturesByGroup[grp.group] || [];
          return (
            <div
              key={grp.group}
              className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden"
              data-testid={`wc26-group-${grp.group.replace(/\s+/g, '-')}`}
            >
              <div className="bg-zinc-100 dark:bg-zinc-800 px-3 py-2 font-bold text-zinc-900 dark:text-zinc-100 flex items-center justify-between">
                <span>{grp.group.replace('Group', 'Groupe')}</span>
                {groupMatches.every(m => m.is_finished) && groupMatches.length === 6 && (
                  <Badge variant="outline" className="text-green-600 dark:text-green-400 border-green-300">Terminé</Badge>
                )}
              </div>
              {/* Classement */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 dark:bg-zinc-800/50 text-zinc-600 dark:text-zinc-400 text-xs">
                    <tr>
                      <th className="px-2 py-1 text-left">#</th>
                      <th className="px-2 py-1 text-left">Équipe</th>
                      <th className="px-1 py-1 text-center" title="Points">Pts</th>
                      <th className="px-1 py-1 text-center" title="Joués">J</th>
                      <th className="px-1 py-1 text-center" title="Victoires">V</th>
                      <th className="px-1 py-1 text-center" title="Nuls">N</th>
                      <th className="px-1 py-1 text-center" title="Défaites">D</th>
                      <th className="px-1 py-1 text-center" title="Buts Pour">BP</th>
                      <th className="px-1 py-1 text-center" title="Buts Contre">BC</th>
                      <th className="px-1 py-1 text-center" title="Différence">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grp.teams.map(t => (
                      <tr key={t.team_id} className="border-t border-zinc-100 dark:border-zinc-800">
                        <td className={`px-2 py-1.5 font-bold ${t.rank <= 2 ? 'text-green-600 dark:text-green-400' : t.rank === 3 ? 'text-orange-500' : 'text-zinc-400'}`}>{t.rank}</td>
                        <td className="px-2 py-1.5 font-medium" data-testid={`wc26-team-${t.team_id}`}>
                          <div className="flex items-center gap-1.5">
                            {t.logo && <img src={t.logo} alt="" className="w-4 h-4" />}
                            <span>{t.team}</span>
                          </div>
                        </td>
                        <td className="px-1 text-center font-bold">{t.points}</td>
                        <td className="px-1 text-center text-zinc-500">{t.played}</td>
                        <td className="px-1 text-center text-zinc-500">{t.win}</td>
                        <td className="px-1 text-center text-zinc-500">{t.draw}</td>
                        <td className="px-1 text-center text-zinc-500">{t.lose}</td>
                        <td className="px-1 text-center text-zinc-500">{t.goals_for}</td>
                        <td className="px-1 text-center text-zinc-500">{t.goals_against}</td>
                        <td className={`px-1 text-center font-medium ${t.goals_diff > 0 ? 'text-green-600 dark:text-green-400' : t.goals_diff < 0 ? 'text-red-500' : 'text-zinc-400'}`}>
                          {t.goals_diff > 0 ? `+${t.goals_diff}` : t.goals_diff}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Matchs */}
              {groupMatches.length > 0 && (
                <div className="px-3 py-2 border-t border-zinc-100 dark:border-zinc-800 space-y-1">
                  {groupMatches.map(fx => {
                    const isLive = liveFixtureIds.has(fx.fixture_id);
                    const isDone = fx.is_finished;
                    return (
                      <div key={fx.fixture_id} className={`flex items-center justify-between text-xs py-1 px-2 rounded ${isLive ? 'bg-red-50 dark:bg-red-950/30' : ''}`}>
                        <span className="font-medium truncate flex-1">{fx.home_team}</span>
                        <span className={`mx-2 font-bold w-12 text-center ${isLive ? 'text-red-600' : isDone ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400'}`}>
                          {isDone || isLive ? `${fx.home_goals ?? 0}-${fx.away_goals ?? 0}` : new Date(fx.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                        </span>
                        <span className="font-medium truncate flex-1 text-right">{fx.away_team}</span>
                        {isLive && (
                          <span className="ml-2 flex items-center gap-1 text-red-500" title="En direct">
                            <Tv className="w-3 h-3" />
                            <span className="font-bold">{fx.elapsed ? `${fx.elapsed}'` : 'LIVE'}</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Note quota */}
      <div className="text-xs text-zinc-400 text-center pt-4">
        <Clock className="inline w-3 h-3 mr-1" />
        Cache 30 min · refresh auto LIVE toutes les 30s · données api-football.com
      </div>
    </div>
  );
};

export default GroupsView;
